<template>
  <div id="networth-recipe-modal">
    <v-dialog :value="isModalActive" max-width="800" @input="inputModal">
      <v-card
        v-if="isModalActive"
        id="networth-recipe-modal-card"
        class="pb-1"
        :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
      >
        <v-card-title>
          {{ $t(`views.netWorth.recipeModal.title`) }}
        </v-card-title>
        <v-card-text class="py-0 mb-0 mt-2">
          <div class="networth-recipe-modal-description my-3" :class="{ 'is-mobile': isMobile }">
            {{ $t(`views.netWorth.recipeModal.description`) }}
          </div>
          <div class="my-0 mb-2 add-group">
            <a @click="!loading && addGroup()">
              <v-icon color="primary">mdi-plus</v-icon>
              {{ $t('views.netWorth.recipeModal.addGroup') }}
            </a>
          </div>
          <div
            v-for="(group, groupIdx) in myRecipe.groups"
            :key="groupIdx"
            class="group"
            :class="{ 'mt-8': groupIdx > 0 }"
          >
            <div class="items-flex align-center">
              <template v-if="myRecipe.groups.length > 1">
                <NetWorthDeleteIcon
                  :idx="groupIdx"
                  :elements="myRecipe.groups"
                  :text="$t('views.netWorth.recipeModal.deleteGroup')"
                  :loading="loading"
                />
                <NetWorthArrows :idx="groupIdx" :elements="myRecipe.groups" :loading="loading" />
              </template>
              <v-text-field
                v-model="group.name"
                :label="$t('views.netWorth.recipeModal.groupNameLabel')"
                :disabled="loading"
                hide-details
              />
            </div>

            <div class="add-account my-3">
              <a @click="!loading && addAccount(group)">
                <v-icon color="primary">mdi-plus</v-icon>
                {{ $t('views.netWorth.recipeModal.addAccount') }}
              </a>
            </div>
            <v-expansion-panels popout>
              <v-expansion-panel v-for="(account, accountIdx) in group.accounts" :key="accountIdx">
                <v-expansion-panel-header>
                  <template v-slot:default="{ open }">
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-tooltip top max-width="300px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-avatar
                              v-bind="attrs"
                              size="7"
                              :color="account.type !== 'custom' ? 'primary' : 'softTextGrey'"
                              class="mr-2"
                              v-on="on"
                            />
                          </template>
                          <span>
                            {{ $t(`views.netWorth.recipeModal.accountType.${account.type}`) }}
                          </span>
                        </v-tooltip>
                        <span class="account-name" :class="{ 'is-mobile': isMobile }">
                          {{ account.name }}
                        </span>
                      </v-col>
                    </v-row>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="items-flex align-center">
                    <template v-if="group.accounts.length > 1">
                      <NetWorthDeleteIcon
                        :idx="accountIdx"
                        :elements="group.accounts"
                        :text="$t('views.netWorth.recipeModal.deleteAccount')"
                        :loading="loading"
                      />
                      <NetWorthArrows
                        :idx="accountIdx"
                        :elements="group.accounts"
                        :loading="loading"
                      />
                    </template>
                    <v-text-field
                      v-model="account.name"
                      :label="$t('views.netWorth.recipeModal.accountNameLabel')"
                      hide-details
                      class="pb-1"
                      :disabled="loading"
                    />
                  </div>
                  <v-select
                    v-model="account.type"
                    :items="accountTypes"
                    :label="$t('views.netWorth.recipeModal.accountType.label')"
                    :disabled="loading"
                    hide-details
                    class="my-4"
                    @change="changeAccountType(account)"
                  ></v-select>

                  <div v-if="account.type === 'custom'" class="my-4 pt-1">
                    <CurrencyInput
                      v-model="account.data.value"
                      :currency="user.baseCurrency"
                      :label="$t('common.value')"
                      :disabled="loading"
                      allowNegative
                    />
                  </div>
                  <div v-else-if="account.type === 'auto'" class="my-4 pt-1">
                    <p class="account-auto-description">
                      {{ $t(`views.netWorth.recipeModal.auto.description`) }}
                      <InfoIcon
                        :size="15"
                        :text="$t(`views.netWorth.recipeModal.auto.descriptionInfo`)"
                        :maxWidth="350"
                      />
                    </p>

                    <div v-if="positionTypes.length">
                      <div class="account-auto-title mr-2">
                        {{ $t('views.netWorth.recipeModal.auto.byTypes') }}:
                      </div>
                      <v-chip-group
                        v-model="account.data.types"
                        active-class="primary"
                        mandatory
                        multiple
                        column
                      >
                        <v-chip
                          v-for="positionTypeElem in positionTypes"
                          :key="positionTypeElem.value"
                          :value="positionTypeElem.value"
                          filter
                          outlined
                        >
                          <span>{{ positionTypeElem.text }}</span>
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <div v-if="brokers.length">
                      <div class="account-auto-title mr-2">
                        {{ $t('views.netWorth.recipeModal.auto.byBrokers') }}:
                        {{
                          account.data.brokers && account.data.brokers.length
                            ? ''
                            : $t('views.netWorth.recipeModal.auto.all')
                        }}
                      </div>
                      <v-chip-group
                        v-model="account.data.brokers"
                        active-class="primary"
                        multiple
                        column
                      >
                        <v-chip
                          v-for="brokerElem in brokers"
                          :key="brokerElem.value"
                          :value="brokerElem.value"
                          filter
                          outlined
                        >
                          <span>{{ brokerElem.text }}</span>
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <div v-if="positionTags.length">
                      <div class="account-auto-title mr-2">
                        {{ $t('views.netWorth.recipeModal.auto.byTags') }}:
                        {{
                          account.data.tags && account.data.tags.length
                            ? ''
                            : $t('views.netWorth.recipeModal.auto.all')
                        }}
                      </div>
                      <v-chip-group v-model="account.data.tags" multiple column>
                        <v-chip
                          v-for="tagElem in positionTags"
                          :key="tagElem"
                          :value="tagElem"
                          filter
                          outlined
                        >
                          <span>{{ tagElem }}</span>
                        </v-chip>
                      </v-chip-group>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-card-actions class="mt-3">
          <v-spacer />
          <v-btn
            class="mr-1"
            text
            :disabled="loading"
            :small="$vuetify.breakpoint.smAndDown"
            @click="closeModal"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="saveDisabled"
            :small="$vuetify.breakpoint.smAndDown"
            @click="saveRecipe"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import gtag from '@/lib/gtag'
import { updateUserNetWorth, getPositionsLean, getBrokers } from '@/lib/api.js'
import { getPositionTypes } from '@/lib/positions/positions.js'
import modalMixin from '@/views/components/core/modalMixin.js'

import InfoIcon from '@/views/components/common/InfoIcon'
import CurrencyInput from '@/views/components/common/CurrencyInput'
import NetWorthArrows from '@/views/components/netWorth/NetWorthArrows'
import NetWorthDeleteIcon from '@/views/components/netWorth/NetWorthDeleteIcon'

export default {
  components: {
    InfoIcon,
    CurrencyInput,
    NetWorthArrows,
    NetWorthDeleteIcon,
  },
  mixins: [modalMixin],
  props: {
    netWorthRecipe: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    myRecipe: null,
    userPositions: [],
    userBrokers: [],
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    saveDisabled() {
      return false
    },
    accountTypes() {
      return ['custom', 'auto'].map((type) => {
        return {
          text: this.$t(`views.netWorth.recipeModal.accountType.${type}`),
          value: type,
        }
      })
    },
    positionTypes() {
      return getPositionTypes({ model: 'position' }).map((positionType) => {
        return {
          text: this.$t(`common.positionTypes.${positionType}`),
          value: positionType,
        }
      })
    },
    positionTags() {
      const tags = []
      this.userPositions.forEach((position) => {
        position.tags.forEach((tag) => {
          if (tags.indexOf(tag) === -1) tags.push(tag)
        })
      })
      return tags
    },
    brokers() {
      return this.userBrokers.map((broker) => {
        return {
          text: broker.alias,
          value: broker._id,
        }
      })
    },
  },
  watch: {
    netWorthRecipe: {
      handler() {
        this.myRecipe = JSON.parse(JSON.stringify(this.netWorthRecipe))
      },
      immediate: true,
      deep: true,
    },
    async isModalActive() {
      if (this.isModalActive) await this.init()
    },
  },
  methods: {
    resetForm() {
      this.loading = false
      this.myRecipe = JSON.parse(JSON.stringify(this.netWorthRecipe))
    },
    async init() {
      const positions = await getPositionsLean()
      this.userPositions = positions.data
      const brokers = await getBrokers()
      this.userBrokers = brokers.data
    },
    async saveRecipe() {
      this.loading = true
      try {
        this.myRecipe.groups.forEach((group) => {
          if (!group.name) group.name = this.$t('views.netWorth.recipeModal.exampleGroupName')
          group.accounts.forEach((account) => {
            if (!account.name)
              account.name = this.$t('views.netWorth.recipeModal.exampleAccountName')
            if (account.type === 'custom' && !account.data.value) {
              account.data.value = 0
            }
          })
        })
        await updateUserNetWorth({ data: { recipe: this.myRecipe } })

        this.$emit('saved')
        this.closeModal()
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })

        gtag.pageview({
          pagePath: `/netWorth/configure/success`,
          pageTitle: `netWorth|configure|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })

        gtag.pageview({
          pagePath: `/netWorth/configure/error`,
          pageTitle: `netWorth|configure|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
    getExampleAccount() {
      return JSON.parse(
        JSON.stringify({
          name: this.$t('views.netWorth.recipeModal.exampleAccountName'),
          type: 'auto',
          data: { types: getPositionTypes({ filterToHide: ['cash'] }) },
        })
      )
    },
    addGroup() {
      this.myRecipe.groups.push({
        name: this.$t('views.netWorth.recipeModal.exampleGroupName'),
        accounts: [this.getExampleAccount()],
      })
    },
    addAccount(group) {
      group.accounts.push(this.getExampleAccount())
    },
    changeAccountType(account) {
      if (account.type === 'auto') {
        delete account.data.value

        this.$set(account.data, 'types', getPositionTypes({ filterToHide: ['cash'] }))
        delete account.data.brokers
        delete account.data.tags
      } else if (account.type === 'custom') {
        delete account.data.types
        delete account.data.brokers
        delete account.data.tags

        this.$set(account.data, 'value', 0)
      }
    },
  },
}
</script>

<style lang="scss">
#networth-recipe-modal-card {
  h1 {
    font-size: 17px;
    font-weight: 400;
  }
  .networth-recipe-modal-description {
    font-size: 14px;
    text-align: justify;
    line-height: 23px;
    &.is-mobile {
      font-size: 13px;
    }
  }
  .add-group {
    font-size: 16px;
    text-align: right;
    font-weight: bold;
  }
  .add-account {
    font-size: 15px;
    text-align: right;
  }
  .account-name {
    font-size: 14px;
    &.is-mobile {
      font-size: 12px;
    }
  }
  .items-flex {
    display: flex;
    &.align-center {
      align-items: center;
    }
  }
  .account-auto-description {
    font-size: 13px;
    line-height: 20px;
  }
  .account-auto-title {
    font-size: 13px;
    font-weight: 400;
  }
}
</style>
